import React, { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { vipAPI } from '../../../services/api';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import ContactFormFields, { emptyContactForm, useContactChannels } from './ContactFormFields';
import ContactHistory, { usePlayerContacts } from './ContactHistory';
import { apiError, contactBody, contactFormErrors, formatShortDay, saveHint, todayISO } from './trackerView';
import { addDays, playerName, savedToast, suggestedFollowUpDays } from './queueView';

// initialForm: the queue suggests a follow-up for the player; the channel follows the last contact
// once the channels are loaded.
const initialForm = (row) => ({ ...emptyContactForm(), nextContactAt: addDays(todayISO(), suggestedFollowUpDays(row)) });

const channelIdByName = (channels, name) => {
  const hit = channels.find((c) => c.channel === name);
  return hit ? String(hit.channel_id) : '';
};

// SavedToast: the confirmation shown after a contact is logged.
export function SavedToast({ text }) {
  return (
    <div role="status" className="flex items-center gap-2.5 rounded-lg border border-cta/40 bg-cta/10 px-4 py-3 text-[13px] font-medium text-cta-light">
      <Check className="h-4 w-4" />{text}
    </div>
  );
}

// ContactLogPanel logs a contact beside the player's history: inside a queue row, or under the
// player card on the profile. onSaved gets the toast text; the caller closes the panel (or, in
// "Work the queue" mode, moves on: saveLabel says so).
export default function ContactLogPanel({ row, onCancel, onSaved, saveLabel = 'Save contact', className = 'mx-5 mb-5' }) {
  const name = playerName(row);
  const history = usePlayerContacts(row.user_id, true);
  const [form, setForm] = useState(() => initialForm(row));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const sectionRef = useRef(null);
  const preselect = (channelId) => setForm((f) => (f.channelId ? f : { ...f, channelId }));
  const { channels, error: channelsError } = useContactChannels(true, preselect);
  const lastChannel = row.last_contact?.channel;

  useEffect(() => {
    if (!lastChannel || channels.length === 0) return;
    const id = channelIdByName(channels, lastChannel);
    if (id) setForm((f) => (f.channelId ? f : { ...f, channelId: id }));
  }, [channels, lastChannel]);

  useEffect(() => {
    sectionRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, []);

  const ready = contactFormErrors(form).length === 0;

  const save = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setError('');
    try {
      await vipAPI.logContact(row.user_id, contactBody(form));
      const channel = channels.find((c) => String(c.channel_id) === form.channelId)?.channel || 'contact';
      onSaved({ toast: savedToast(channel, name, form.nextContactAt) });
    } catch (err) {
      setError(apiError(err, 'Failed to save the contact.'));
      setSaving(false);
    }
  };

  const shownError = error || channelsError;
  const last = row.last_contact;
  return (
    <section
      ref={sectionRef} aria-label={`Log contact with ${name}`}
      className={cn('overflow-hidden rounded-2xl border border-white/15 bg-surface-1', className)}
    >
      <div className="flex items-center gap-3.5 border-b border-white/10 px-6 py-4">
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3.5 gap-y-1.5">
          <h3 className="text-[17px] font-bold text-ink">Log contact with {name}</h3>
          <span className="text-[13px] text-ink-dim">
            Last contact: <span className="text-ink">{last ? `${formatShortDay(last.contacted_at)} · ${last.channel}` : 'never'}</span>
          </span>
        </div>
        <Button type="button" variant="ghost" size="icon" aria-label="Close" className="h-11 w-11" onClick={onCancel}>
          <X className="h-[18px] w-[18px]" />
        </Button>
      </div>

      <div className="flex flex-wrap">
        <form className="min-w-0 flex-[999_1_520px] px-6 py-5" onSubmit={(e) => { e.preventDefault(); save(); }}>
          <ContactFormFields form={form} onChange={setForm} channels={channels} who={name} onSubmitShortcut={save} />
        </form>
        <ContactHistory
          name={name} userId={row.user_id} contacts={history.contacts} loading={history.loading} error={history.error}
          className="min-w-0 flex-[1_1_440px] border-t border-white/10 md:border-l md:border-t-0"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 border-t border-white/10 px-6 py-3.5">
        <div className={cn('flex-1 text-sm', shownError ? 'text-negative' : 'text-ink-dim')} role={shownError ? 'alert' : undefined}>
          {shownError || saveHint(form)}
        </div>
        <Button type="button" variant="outline" className="h-11" onClick={onCancel}>Cancel</Button>
        <Button type="button" className="h-11 gap-2" disabled={!ready || saving} onClick={save}>
          {saving ? 'Saving…' : saveLabel}
          <span className="text-xs font-medium opacity-75">⌘↵</span>
        </Button>
      </div>
    </section>
  );
}
